import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

@Injectable()
export class LoginService {
    private baseUrl: string;

    constructor(private http: HttpClient) {
        this.baseUrl = environment.API_BACK;
    }

    private getLang(): string {
        return sessionStorage.getItem('lang') || 'es';
    }

    public login(username: string, password: string, type: string): Observable<any> {
        const url = `${this.baseUrl}login/login?lang=${this.getLang()}`;
        const data = {
            username: username,
            password: password,
            type: type
        };

        console.debug('[LoginService] Intentando login con URL:', url);
        console.debug('[LoginService] Datos de login:', { username, type });

        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this.http.post(url, data, {
            headers,
            withCredentials: true
        }).pipe(
            map((response: any) => {
                console.debug('[LoginService] Respuesta del servidor:', response);
                if (response.success) {
                    console.debug('[LoginService] Login exitoso, guardando sesión');

                    // Solo guardamos los datos básicos de sesión
                    const sessionData = {
                        user_id: response.data.user_id,
                        username: response.data.username,
                        token: response.data.token
                    };

                    this.setUserSession(sessionData);
                    return response;
                }
                console.error('[LoginService] Login fallido:', response);
                return throwError(response);
            }),
            catchError(error => {
                console.error('[LoginService] Error en login:', error);
                let errorMessage = 'Error en el inicio de sesión';

                if (error.status === 500) {
                    errorMessage = 'Error interno del servidor. Por favor, inténtelo de nuevo más tarde.';
                    // Registrar el error para debugging
                    console.error('[LoginService] Error 500:', error.error);
                } else if (error.status === 401) {
                    errorMessage = 'Usuario o contraseña incorrectos';
                } else if (error.status === 400) {
                    errorMessage = error.error && error.error.message ? error.error.message : 'Datos de login inválidos';
                }

                return throwError({
                    success: false,
                    message: errorMessage,
                    originalError: error
                });
            })
        );
    }

    private setUserSession(data: { user_id: string; username: string; token: string }): void {
        if (!data || !data.user_id || !data.username || !data.token) {
            console.error('[LoginService] Datos de sesión inválidos');
            this.clearSession();
            return;
        }

        try {
            console.debug('[LoginService] Guardando datos de sesión:', data);

            // Guardar token y establecer tiempo de expiración (30 días)
            const tokenExpiration = new Date();
            tokenExpiration.setDate(tokenExpiration.getDate() + 30);

            const tokenData = {
                token: data.token,
                expiration: tokenExpiration.getTime()
            };

            console.debug('[LoginService] Token a guardar:', {
                token: data.token,
                expiration: new Date(tokenExpiration).toISOString()
            });

            // Guardar datos básicos de sesión
            localStorage.setItem('auth_data', JSON.stringify(tokenData));
            localStorage.setItem('user_id', data.user_id);
            localStorage.setItem('username', data.username);
            localStorage.setItem('objsession', JSON.stringify({
                id: data.user_id,
                username: data.username,
                lastActivity: new Date().getTime()
            }));

            // Verificar que se guardó correctamente
            const savedToken = this.getToken();
            console.debug('[LoginService] Token recuperado después de guardar:', {
                token: savedToken,
                matches: savedToken === data.token
            });

        } catch (error) {
            console.error('[LoginService] Error al guardar datos de sesión:', error);
            this.clearSession();
            throw error;
        }
    }

    private clearSession(): void {
        localStorage.removeItem('auth_data');
        localStorage.removeItem('user_id');
        localStorage.removeItem('username');
        localStorage.removeItem('objsession');
        localStorage.removeItem('infoClient');
    }

    public getToken(): string | null {
        const authData = localStorage.getItem('auth_data');
        if (!authData) return null;

        try {
            const { token, expiration } = JSON.parse(authData);
            if (new Date().getTime() > expiration) {
                this.clearSession();
                return null;
            }
            return token;
        } catch {
            this.clearSession();
            return null;
        }
    }

    public getHeaders(requiresAuth: boolean = false): HttpHeaders {
        let headers = new HttpHeaders().set('Content-Type', 'application/json');
        if (requiresAuth) {
            const token = this.getToken();
            if (token) {
                console.debug('[LoginService] Enviando token en headers:', { token });
                headers = headers.set('Authorization', token);
            } else {
                console.warn('[LoginService] Token no encontrado para petición autenticada');
            }
        }
        return headers;
    }

    public recoveryPassword(email: string): Observable<any> {
        const url = `${this.baseUrl}login/remember_password/`;
        const data = {
            email: email,
            lang: this.getLang()
        };

        return this.http.post(url, data, {
            headers: this.getHeaders()
        }).pipe(
            catchError(error => throwError(error))
        );
    }

    public getOfferClient(): Observable<any> {
        const url = `${this.baseUrl}alert/list?lang=${this.getLang()}`;

        return this.http.get(url, {
            headers: this.getHeaders(true)
        }).pipe(
            catchError(error => throwError(error))
        );
    }

    public doRegister(obj: any): Observable<any> {
        const url = `${this.baseUrl}login/register`;

        return this.http.post(url, obj, {
            headers: this.getHeaders()
        }).pipe(
            catchError(error => throwError(error))
        );
    }

    public sendStadistic(obj: any): Observable<any> {
        const url = `${this.baseUrl}interaction/add`;

        return this.http.post(url, obj, {
            headers: this.getHeaders(true)
        }).pipe(
            catchError(error => throwError(error))
        );
    }

    public logout(): void {
        this.setAnalyticsValue('Logout');
        localStorage.clear();
        sessionStorage.clear();
    }

    public setAnalyticsValue(pageNow: string): void {
        const pageLater = localStorage.getItem('pageLater') || 'Login';
        const sessionStr = localStorage.getItem('objsession');

        if (!sessionStr) return;

        try {
            const sessionData = JSON.parse(sessionStr);
            const analyticsData = {
                session_id: sessionData.id,
                page_before: pageLater,
                page_now: pageNow,
                lang: this.getLang()
            };

            this.sendStadistic(analyticsData).subscribe(
                () => localStorage.setItem('pageLater', pageNow),
                error => throwError(error)
            );
        } catch (error) {
            throwError(error);
        }
    }
}
